import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	Output,
	ViewChild,
	EventEmitter,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import {
	BROKER_LANE_DETAILS_TABLE,
	BrokerLaneDetails,
	CarrierFeedback,
	CarrierNotes,
	DATE_MDY,
	IS_NOT_EMPTY,
	IS_NULL_OR_UNDEFINED,
	RestService,
	TruckLaneSearchResults,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { PLAN_PRODUCTS } from '@domain/constants/app-variables';
import { SharedService } from '@service/shared/shared.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CarrierDataFeedbackDialog } from '../../project/capacity-search-panel/carrier-data-feedback/carrier-data-feedback.component';
import { environment } from '@environment/environment';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { NotesService } from '@service/notesService/notes.service';
import { ProductsService } from '@service/products/products.service';

@Component({
	selector: 'app-broker-lane-details',
	templateUrl: './broker-lane-details.component.html',
	styleUrls: ['./broker-lane-details.component.scss'],
	providers: [CurrencyPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrokerLaneDetailsComponent implements OnInit {
	@Input() carrierLaneData: any = null;
	@Input() recentFeedbackList: Array<CarrierFeedback> = null;
	@Input() incorrectLaneFeedback: boolean = false;
	@Input() carrierNotesList: Array<CarrierNotes> = null;
	@Input() selectedLane: BrokerLaneDetails = null;
	@Output() laneSelected: EventEmitter<BrokerLaneDetails> = new EventEmitter<BrokerLaneDetails>();

	lanesList: Array<any> = null;
	lanesLength: number = 0;
	lanesCurrentPage: number = 0;
	LANES_PAGE_SIZE: number = 20;

	lanesDataTable = new MatTableDataSource<BrokerLaneDetails>([]);
	@ViewChild(MatPaginator) lanePaginator: MatPaginator;
	@ViewChild(MatSort) laneSort: MatSort;

	lanesColumns = [
		'origin',
		'destination',
		'equipment',
		'frequency',
		'partial',
		'lastMatch',
		'rateUpdatedAt',
		'mostRecentActivity',
	];
	IS_PRICING_ENABLED: boolean = false;
	isLoadingLanesList: boolean = false;

	constructor(
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private cargochief: Cargochief,
		private sharedService: SharedService,
		public dialog: MatDialog,
		public accountSettings: AccountSettingsService,
		private notesService: NotesService,
		public productsService: ProductsService
	) {}

	ngOnInit() {
		this.lanesDataTable = new MatTableDataSource<BrokerLaneDetails>([]);
		this.initializePage();
		this.initializeSort();
		this.IS_PRICING_ENABLED = this.cargochief.isProductEnabled(PLAN_PRODUCTS.PRICING);
		if (
			this.IS_PRICING_ENABLED &&
			environment.CARRIER_RATES &&
			this.productsService.CARRIER_RATES &&
			this.accountSettings.CARRIER_RATES
		) {
			this.lanesColumns.splice(this.lanesColumns.length - 2, 0, 'lastRate');
		}
		if (this.incorrectLaneFeedback) {
			this.lanesColumns.push('laneFeedback');
		}
		this.updateLanesNotesCount();
	}

	ngOnChanges() {
		if (
			!this.utilService.isNullOrUndefined(this.carrierLaneData) &&
			!this.utilService.isNullOrUndefined(this.carrierLaneData.lane) &&
			this.utilService.isNullOrUndefined(this.lanesList) &&
			!this.isLoadingLanesList
		) {
			this.getCarrierLanes(0);
		}
	}

	initializePage() {
		this.lanesDataTable.paginator = this.lanePaginator;
	}

	initializeSort() {
		this.lanesDataTable.sort = this.laneSort;
	}

	showProgressBar() {
		return this.isLoadingLanesList;
	}

	getCarrierLanes(pageNumber) {
		if (!this.utilService.isNullOrUndefined(pageNumber)) {
			this.isLoadingLanesList = true;
			const url = this.urlService.getLanesList();
			const payload = {
				page: pageNumber,
				pageSize: this.LANES_PAGE_SIZE,
				carrierId: this.carrierLaneData.lane.carrierId,
			};
			this.restService.POST(url, payload).subscribe(
				(result) => {
					this.lanesList = result['data'];
					this.lanesLength = result.numberOfResults;
					this.buildView();
					if (this.lanesLength > 0) {
						if (!this.utilService.isNullOrUndefined(this.recentFeedbackList)) {
							this.updateRatingAndLoadNotBookedReason();
						}
					}
					if (this.utilService.isNullOrUndefined(this.lanePaginator)) {
						this.initializePage();
					}
					this.initializeSort();
					this.updateLanesNotesCount();
					this.isLoadingLanesList = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Cannot fetch carrier lanes.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isLoadingLanesList = false;
				}
			);
		}
	}

	buildView() {
		this.lanesDataTable = new MatTableDataSource<BrokerLaneDetails>(
			BROKER_LANE_DETAILS_TABLE(this.lanesList)
		);
	}

	notesAdded(notes: CarrierNotes) {
		this.notesService.sendNotesAddedEvent(notes);
	}

	updateRatingAndLoadNotBookedReason() {
		this.lanesDataTable.data.map((lane: BrokerLaneDetails) => {
			const laneFeedback = this.recentFeedbackList.filter((feedback: CarrierFeedback) => {
				return feedback.truckId == lane.laneId;
			});
			if (laneFeedback.length > 0) {
				lane.loadNotBookedReason = !this.utilService.isNullOrUndefined(
					laneFeedback[0].loadNotBookedReason
				)
					? DATE_MDY(laneFeedback[0].lastModified) +
						', ' +
						this.getLoadNotBookedReason(laneFeedback[0])
					: null;
				lane.rating = laneFeedback[0].rating;
			}
		});
	}

	getLoadNotBookedReason(carrierFeedback: CarrierFeedback): string {
		if (carrierFeedback.booked) {
			return 'Booked';
		} else {
			return `Not Booked ${
				!this.utilService.isNullOrUndefined(carrierFeedback.loadNotBookedReason)
					? '(' + carrierFeedback.loadNotBookedReason + ')'
					: ''
			}`;
		}
	}

	laneFeedback(lane: BrokerLaneDetails) {
		const existingLane = this.lanesList.find((l) => l.id === lane.laneId);
		let laneSelected: any = Object.assign({}, existingLane);

		laneSelected.businessName = this.carrierLaneData?.lane?.businessName;
		laneSelected.firmMc = this.carrierLaneData?.lane?.firmMc;
		laneSelected.firmDot = this.carrierLaneData?.lane?.firmDot;
		laneSelected.id = lane.laneId;

		laneSelected.origin = {
			city: laneSelected?.originCity,
			state: laneSelected?.originState,
		};
		laneSelected.destination = {
			city: laneSelected?.destCity,
			state: laneSelected?.destState,
		};
		laneSelected.trailerType = this.carrierLaneData?.lane?.trailerTypes;
		const dialogRef = this.dialog.open(CarrierDataFeedbackDialog, {
			width: '900px',
			data: {
				lane: laneSelected,
				incorrectDataType: 'lane',
			},
		});
	}

	updateLanesNotesCount() {
		if (
			!this.utilService.isNullOrUndefined(this.lanesDataTable) &&
			this.lanesDataTable.data.length > 0 &&
			!this.utilService.isNullOrUndefined(this.carrierNotesList)
		) {
			this.lanesDataTable.data.map((lane) => {
				lane.notesCount = this.carrierNotesList.filter((note) => {
					return note.truckLaneId == lane.laneId;
				}).length;
			});
		}
	}

	rateTooltip(row: BrokerLaneDetails): string {
		if (IS_NULL_OR_UNDEFINED(row.rateUpdatedAt)) {
			return '';
		}
		return `Rate Updated At: ${DATE_MDY(row.rateUpdatedAt)}`;
	}

	onRowClick(lane: BrokerLaneDetails) {
		this.laneSelected.emit(lane);
	}
}
